import { useMemo } from 'react'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import effectiveResearchIds from '../../helpers/measurementSignals/effectiveResearchIds'

// AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md

/**
 * De onderzoeken waaraan één huishouden of medewerker meedoet — voor schermen
 * die de onderzoeken van de organisatie niet zelf al laden (de effectgrafieken
 * van een medewerker, de teller op het agenda-tabblad).
 *
 * Wikkelt `effectiveResearchIds`; de regel zelf staat dáár en nergens anders.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * De teruggegeven array houdt dezelfde identiteit zolang de ínhoud gelijk
 * blijft — ook als de aanroeper elke render een verse `[]` meegeeft. Dat is
 * geen luxe: `DataType` zet de lijst in zijn dependency-array, en een nieuwe
 * array per render is daar een ophaal-lus.
 * AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
 *
 * `loaded` is een eigen waarde: zolang de onderzoeken onderweg zijn komt
 * alleen de handmatige set terug, en mag het scherm niet beweren dat er
 * "niets gekoppeld" is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} args
 * @param {'adress'|'persona'} args.kind - Soort entiteit
 * @param {Array<string>} [args.linkedIds] - `Adresses.Research` / `Personas.Research`
 * @param {boolean} [args.enabled] - `false` zet geen listener op; voor schermen die
 *   voor élk soort record gemount worden en het alleen bij één soort nodig hebben
 * @returns {{ids: Array<string>, loaded: boolean}}
 */
const useEffectiveResearchIds = ({ kind, linkedIds, enabled = true } = {}) => {
    const researches = useFirestoreQuery('Research', { orderBy: ['Position', 'asc'], enabled })

    // Sleutel op de inhoud, niet op de array: zo overleeft de identiteit een
    // verse `[]` van de aanroeper.
    const linkedKey = (Array.isArray(linkedIds) ? linkedIds : []).join('|')

    // Ook de automatische helft als inhoudssleutel: een nieuwe Firestore-snapshot
    // van Research geeft anders elke keer een nieuwe array, ook als er niets
    // veranderde.
    const autoKey = Array.isArray(researches)
        ? effectiveResearchIds({ kind, linkedIds: [], researches }).join('|')
        : ''
    const ids = useMemo(
        () => [...new Set([...(linkedKey ? linkedKey.split('|') : []), ...(autoKey ? autoKey.split('|') : [])])],
        [linkedKey, autoKey]
    )

    return { ids, loaded: Array.isArray(researches) }
}

export default useEffectiveResearchIds
